/**
 * HistoricoVersoes — versões do prompt de um agente, com o diff contra a anterior do
 * mesmo escopo (A1 FR-17). Usado no editor do Squad Designer (projeto + tenant) e na
 * ficha do Catálogo (só tenant).
 *
 *   GET /agents/{nome}/prompt/versions?scope=&project_id=
 *   GET /agents/{nome}/prompt/versions/{id}   → { texto, diff_anterior }
 */
import { useState } from 'react';
import { C } from '@/theme/palette';
import { usePromptVersoes, usePromptVersao } from '@/api/agents';
import { itensDe } from './comum';

function LinhaDoDiff({ linha }) {
  let cor = C.neutralDim;
  if (linha.startsWith('+') && !linha.startsWith('+++')) cor = C.cyber;
  else if (linha.startsWith('-') && !linha.startsWith('---')) cor = C.hot;
  else if (linha.startsWith('@@')) cor = C.quantum;
  return <div style={{ color: cor, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{linha || ' '}</div>;
}

function DiffDaVersao({ nome, versionId }) {
  const q = usePromptVersao(nome, versionId);
  if (q.isLoading) return <div style={{ fontSize: 12, color: C.neutralMuted }}>Carregando diff…</div>;
  if (q.isError) return <div style={{ fontSize: 12, color: C.hot }}>Não foi possível carregar a versão.</div>;
  const diff = q.data?.diff_anterior;
  return (
    <div data-testid="diff-versao"
      style={{ marginTop: 6, padding: 10, borderRadius: 6, background: C.black, border: `1px solid ${C.blackBorder}`,
        fontFamily: 'monospace', fontSize: 11.5, maxHeight: 260, overflowY: 'auto' }}>
      {typeof diff === 'string' && diff
        ? diff.split('\n').map((l, i) => <LinhaDoDiff key={i} linha={l} />)
        : <div style={{ color: C.neutralMuted }}>Primeira versão deste escopo — sem diff anterior.</div>}
    </div>
  );
}

function ListaDoEscopo({ nome, titulo, scope, projectId }) {
  const q = usePromptVersoes(nome, { scope, projectId });
  const [aberta, setAberta] = useState(null);
  const itens = itensDe(q.data).filter((v) => v.version_id);
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11, color: C.neutralMuted, letterSpacing: '0.05em', fontWeight: 600, marginBottom: 6 }}>
        {titulo.toUpperCase()} ({itens.length})
      </div>
      {q.isLoading && <div style={{ fontSize: 12, color: C.neutralMuted }}>Carregando versões…</div>}
      {q.isError && <div style={{ fontSize: 12, color: C.hot }}>Não foi possível carregar o histórico.</div>}
      {!q.isLoading && !q.isError && itens.length === 0 && (
        <div style={{ fontSize: 12, color: C.neutralDim }}>Nenhuma versão neste escopo.</div>
      )}
      {itens.map((v) => (
        <div key={v.version_id} style={{ padding: '6px 0', borderTop: `1px solid ${C.blackBorder}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
            <strong style={{ color: C.neutral }}>v{v.version}</strong>
            <span style={{ color: C.neutralMuted }}>{v.origem}</span>
            <span style={{ color: C.neutralMuted }}>{Number(v.chars || 0).toLocaleString('pt-BR')} caracteres</span>
            {v.secoes_ok && (
              <span style={{ color: v.secoes_ok.ok ? C.cyber : C.warn }}>
                {v.secoes_ok.ok ? '✅ especialista' : '⚠️ incompleto'}
              </span>
            )}
            {v.tem_conflito && <span style={{ color: C.warn }}>⚠️ conflito com regras</span>}
            <span style={{ color: C.neutralMuted }}>
              {v.author?.nome ? `${v.author.nome} · ` : ''}
              {v.created_at ? new Date(v.created_at).toLocaleString('pt-BR') : ''}
            </span>
            <button type="button" onClick={() => setAberta(aberta === v.version_id ? null : v.version_id)}
              aria-label={`Diff da versão ${v.version} (${titulo})`}
              style={{ marginLeft: 'auto', background: 'transparent', border: `1px solid ${C.blackBorder}`,
                color: C.cyber, borderRadius: 5, padding: '2px 8px', fontSize: 11.5, cursor: 'pointer' }}>
              {aberta === v.version_id ? 'Ocultar diff' : 'Ver diff'}
            </button>
          </div>
          {aberta === v.version_id && <DiffDaVersao nome={nome} versionId={v.version_id} />}
        </div>
      ))}
    </div>
  );
}

/**
 * @param {{ nome: string, projectId?: string|null, escopos?: Array<'project'|'tenant'> }} props
 */
export function HistoricoVersoes({ nome, projectId = null, escopos = ['project', 'tenant'] }) {
  return (
    <div aria-label="Histórico de versões do prompt" role="region">
      {escopos.includes('project') && projectId && (
        <ListaDoEscopo nome={nome} titulo="Versões do projeto" scope="project" projectId={projectId} />
      )}
      {escopos.includes('tenant') && (
        <ListaDoEscopo nome={nome} titulo="Versões do tenant" scope="tenant" />
      )}
    </div>
  );
}
